import React, { useCallback, useContext, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, Moon, Sun, User } from 'lucide-react'
import { cn } from '../../lib/utils/cn'
import { Button } from '../ui/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/DropdownMenu'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../ui/Sheet'
import { useAuthLogout } from '../../api/auth/useAuthLogout'
import { useDispatch } from 'react-redux'
import { setAuthenticated } from '../../store/reducers/userReducer'
import { ThemeContext } from '../../context/ThemeProvider' // Import the ThemeContext

const navItems = [
  { name: 'Home', href: 'home' },
  { name: 'My Uploads', href: 'uploads' },
]

const profileItems = [
  { name: 'Profile', href: 'profile' },
  { name: 'Support', href: 'support' },
]

export function TopNavbar(): JSX.Element {
  const location = useLocation()
  const [isProfileOpen, setIsProfileOpen] = useState(false)
  const { mutateAsync: logout } = useAuthLogout()
  const dispatch = useDispatch()
  const { theme, toggleTheme } = useContext(ThemeContext)

  const handleLogout = useCallback(() => {
    logout()
    dispatch(setAuthenticated(false))
  }, [dispatch, logout])

  return (
    <header className="absolute px-4 sm: px-8 sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 shadow-lg dark:bg-slate-900">
      <div className="flex h-14 items-center justify-between w-full ">
        <div className="hidden md:flex items-center">
          <Link to="/" className="pr-6 flex items-center space-x-2">
            <img
              className="object-scale-down h-6"
              src={`/capgains-icon-${theme}.png`}></img>
            <span className="hidden font-bold sm:inline-block">My Cap Gains</span>
          </Link>
          <nav className="flex items-center space-x-6 text-sm font-medium">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  "border-b-2 border-transparent transition-colors hover:text-foreground/80 relative w-fit block after:block after:content-[''] after:absolute after:h-[2px] after:bg-foreground/80 after:w-full after:scale-x-0 after:hover:scale-x-100 after:transition after:duration-300 after:origin-center",
                  location.pathname.includes(`dashboard/${item.href}`)
                    ? 'text-foreground border-foreground/80'
                    : 'text-foreground/60',
                )}>
                {item.name}
              </Link>
            ))}
          </nav>
        </div>
        <Sheet open={isProfileOpen} onOpenChange={setIsProfileOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              className="mr-2 px-0 text-base hover:bg-transparent focus-visible:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 md:hidden">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle Menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="pr-0 dark:bg-slate-700">
            <SheetHeader>
              <SheetTitle>Menu</SheetTitle>
            </SheetHeader>
            <nav className="flex flex-col space-y-4 mt-4">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    'text-foreground/60 transition-colors hover:text-foreground dark:text-slate-400 dark:hover:text-white',
                    location.pathname === item.href && 'text-foreground dark:text-white',
                  )}
                  onClick={(e) => {
                    setIsProfileOpen(false)
                  }}>
                  {item.name}
                </Link>
              ))}
            </nav>
          </SheetContent>
        </Sheet>
        <div className="flex flex-1 items-center justify-end">
          <nav className="flex flex-row gap-2 items-center">
            <button onClick={() => toggleTheme()}>
              {theme === 'dark' && <Sun />}
              {theme === 'light' && <Moon />}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full">
                  <User className="h-5 w-5" />
                  <span className="sr-only">User menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {profileItems.map((item) => {
                  return (
                    <DropdownMenuItem>
                      {item.href === 'support' ? (
                        <button
                          className="relative w-fit block after:block after:content-[''] after:absolute after:h-[2px] after:bg-black after:dark:bg-white after:w-full after:scale-x-0 after:hover:scale-x-100 after:transition after:duration-300 after:origin-center"
                          onClick={(e) => {
                            e.preventDefault()
                            window.open(
                              `mailto:mycapgains@gmail.com?subject=[CapGains] Support Ticket`,
                              '_blank',
                            )
                          }}>
                          {item.name}
                        </button>
                      ) : (
                        <Link
                          className="relative w-fit block after:block after:content-[''] after:absolute after:h-[2px] after:bg-black after:dark:bg-white after:w-full after:scale-x-0 after:hover:scale-x-100 after:transition after:duration-300 after:origin-center"
                          to={item.href}>
                          {item.name}
                        </Link>
                      )}
                    </DropdownMenuItem>
                  )
                })}
                <DropdownMenuSeparator />
                <DropdownMenuItem>
                  <Button variant={'destructive'} onClick={handleLogout}>
                    Log Out
                  </Button>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
        </div>
      </div>
    </header>
  )
}
